import React, { useRef, useState } from 'react';
import moment from 'moment';
import Icon from '../common/Icon';
import { icons } from '../../config/icons';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { usePopupMenu } from '../../context/PopupMenuContext';
import { useModal } from '../../context/ModalContext';
import PopupDropdownMenu from '../layout/PopupDropdownMenu';

export const FormContext = React.createContext({});

const CmsElementRenderer = ({ element, sectionId, parentPath = '', dynamicData = null }) => {
    // Hooks must be called unconditionally — before any early return
    const elementRef = useRef(null);
    const formParentProps = React.useContext(FormContext);
    const { openPopupMenu, closePopupMenu } = usePopupMenu();
    const { showAlert } = useModal();
    const rawProperties = element ? ((typeof element.properties === 'string' && element.properties) ? JSON.parse(element.properties) : (element.properties || {})) : {};
    const rawAnimation = rawProperties.animation;
    const animationSettings = rawAnimation ? (typeof rawAnimation === 'string' ? JSON.parse(rawAnimation) : rawAnimation) : {};
    useScrollAnimation(elementRef, animationSettings);

    const replacePlaceholders = (str) => {
        if (!str || typeof str !== 'string' || !dynamicData) return str;
        return str.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
            const trimmedKey = key.trim();
            const parts = trimmedKey.split('|').map(p => p.trim());
            const dataKey = parts[0];
            let value = dynamicData[dataKey];
            
            if (value !== undefined) {
                if (parts.length > 1) {
                    const filter = parts[1];
                    if (filter.startsWith('date')) {
                        let format = 'MMM DD, YYYY';
                        if (filter.includes(':')) {
                            format = filter.substring(filter.indexOf(':') + 1).trim();
                        }
                        if (moment(value).isValid()) {
                            return moment(value).format(format);
                        }
                    }
                }
                return value;
            }
            return match;
        });
    };

    // State for form_container (must be before any early return)
    const [formState, setFormState] = useState({ loading: false, success: false, error: null });

    if (!element) return null;

    const isLayout = element.element_type === 'container' || element.element_type === 'form_container' || element.element_type === 'dynamic_data_grid';
    const rawResponsive = (typeof element.responsive === 'string' && element.responsive) ? JSON.parse(element.responsive) : (element.responsive || {});

    const tProps = rawResponsive?.tablet?.properties || {};
    const mProps = rawResponsive?.mobile?.properties || {};
    const elements = (typeof element.elements === 'string' && element.elements) ? JSON.parse(element.elements) : (element.elements || []);

    // CSS variable mapping — output all viewport variants
    const bVars = {};

    // Helper to set a CSS variable
    const setVar = (varName, value) => {
        if (value !== undefined && value !== null && value !== '') {
            bVars[varName] = value;
        }
    };

    // Map base properties to CSS variables
    const baseProps = dynamicData 
        ? Object.fromEntries(Object.entries(rawProperties).map(([k, v]) => [k, typeof v === 'string' ? replacePlaceholders(v) : v]))
        : rawProperties;
    setVar('--b-text-align', baseProps['text-align']);
    setVar('--b-list-style-type', baseProps['list-style-type']);
    setVar('--b-color', baseProps['color']);
    setVar('--b-font-size', baseProps['font-size']);
    setVar('--b-font-weight', baseProps['font-weight']);
    setVar('--b-font-style', baseProps['font-style']);
    setVar('--b-text-decoration', baseProps['text-decoration']);
    setVar('--b-text-transform', baseProps['text-transform']);
    setVar('--b-line-height', baseProps['line-height']);
    setVar('--b-letter-spacing', baseProps['letter-spacing']);
    setVar('--b-margin-bottom', baseProps['margin-bottom']);
    setVar('--b-margin-top', baseProps['margin-top']);
    setVar('--b-margin', baseProps['margin']);
    setVar('--b-padding', baseProps['padding']);
    setVar('--b-border-radius', baseProps['border-radius']);
    // Use layout-specific variable for containers to prevent CSS cascade to children
    if (isLayout) {
        setVar('--b-layout-bg-color', baseProps['bg-color']);
    } else {
        setVar('--b-bg-color', baseProps['bg-color']);
    }
    if (baseProps['width']) {
        bVars['--b-width'] = baseProps['width'];
        bVars.width = baseProps['width'];
    }
    setVar('--b-height', baseProps['height']);
    setVar('--b-min-width', baseProps['min-width']);
    setVar('--b-max-width', baseProps['max-width']);
    setVar('--b-min-height', baseProps['min-height']);
    setVar('--b-max-height', baseProps['max-height']);
    setVar('--b-gap', baseProps['gap']);
    setVar('--b-justify-content', baseProps['justify-content']);
    setVar('--b-flex-direction', baseProps['flex-direction']);
    setVar('--b-align-items', baseProps['align-items']);
    setVar('--b-flex-wrap', baseProps['flex-wrap']);
    setVar('--b-border-width', baseProps['border-width']);
    setVar('--b-border-style', baseProps['border-style']);
    setVar('--b-border-color', baseProps['border-color']);
    setVar('--b-bg-position', baseProps['bg-position']);
    setVar('--b-bg-repeat', baseProps['bg-repeat']);
    setVar('--b-bg-size', baseProps['bg-size']);
    setVar('--b-box-shadow', baseProps['box-shadow']);
    setVar('--b-transition', baseProps['transition']);
    setVar('--b-opacity', baseProps['opacity']);
    setVar('--b-overflow', baseProps['overflow']);
    
    if (baseProps['overflow']) bVars.overflow = baseProps['overflow'];

    if (baseProps['flex-direction']) bVars.flexDirection = baseProps['flex-direction'];
    if (baseProps['justify-content']) bVars.justifyContent = baseProps['justify-content'];
    if (baseProps['align-items']) bVars.alignItems = baseProps['align-items'];
    if (baseProps['flex-wrap']) bVars.flexWrap = baseProps['flex-wrap'];
    if (baseProps['gap']) bVars.gap = baseProps['gap'];

    if (baseProps['display']) {
        bVars.display = baseProps['display'];
    } else if (
        baseProps['flex-direction'] ||
        baseProps['justify-content'] ||
        baseProps['align-items'] ||
        baseProps['flex-wrap']
    ) {
        bVars.display = 'flex';
    }
    if (baseProps['grid-template-columns']) bVars.gridTemplateColumns = baseProps['grid-template-columns'];

    // Background image + overlay (replaces JS gradient composition)
    if (baseProps['bg-image']) {
        setVar('--b-bg-image', `url(${baseProps['bg-image']})`);
    }
    if (baseProps['overlay-color']) {
        setVar('--b-bg-overlay', baseProps['overlay-color']);
    }
    if (baseProps['overlay-opacity'] !== undefined && baseProps['overlay-opacity'] !== '') {
        setVar('--b-bg-opacity', baseProps['overlay-opacity']);
    }

    // Interaction state CSS variables (renamed to match --b-* convention)
    if (isLayout) {
        setVar('--b-hover-layout-bg-color', baseProps['hover-bg-color']);
        setVar('--b-active-layout-bg-color', baseProps['active-bg-color']);
        setVar('--b-focus-layout-bg-color', baseProps['focus-bg-color']);
    } else {
        setVar('--b-hover-bg-color', baseProps['hover-bg-color']);
        setVar('--b-active-bg-color', baseProps['active-bg-color']);
        setVar('--b-focus-bg-color', baseProps['focus-bg-color']);
    }
    setVar('--b-hover-color', baseProps['hover-color']);
    setVar('--b-hover-border-color', baseProps['hover-border-color']);

    // Hover/active/focus font-size, font-weight, font-style
    setVar('--b-hover-font-size', baseProps['hover-font-size']);
    setVar('--b-hover-font-weight', baseProps['hover-font-weight']);
    setVar('--b-hover-font-style', baseProps['hover-font-style']);
    setVar('--b-hover-text-decoration', baseProps['hover-text-decoration']);
    setVar('--b-active-font-size', baseProps['active-font-size']);
    setVar('--b-active-font-weight', baseProps['active-font-weight']);
    setVar('--b-focus-font-size', baseProps['focus-font-size']);
    setVar('--b-focus-font-weight', baseProps['focus-font-weight']);

    // Map interaction state transitions
    setVar('--b-hover-transition', baseProps['hover-transition']);
    setVar('--b-active-transition', baseProps['active-transition']);
    setVar('--b-focus-transition', baseProps['focus-transition']);

    // Generate dynamic transform and filter variables
    const states = [{ prefix: '', propPrefix: '' }, { prefix: 'hover-', propPrefix: 'hover-' }, { prefix: 'active-', propPrefix: 'active-' }, { prefix: 'focus-', propPrefix: 'focus-' }];
    states.forEach(({ prefix, propPrefix }) => {
        let t = [];
        const scale = baseProps[`${propPrefix}transform-scale`] ?? baseProps[`${propPrefix}transformScale`];
        const rotate = baseProps[`${propPrefix}transform-rotate`] ?? baseProps[`${propPrefix}transformRotate`];
        const translateY = baseProps[`${propPrefix}transform-translate-y`] ?? baseProps[`${propPrefix}transformTranslateY`];
        
        if (scale) t.push(`scale(${scale})`);
        if (rotate) t.push(`rotate(${rotate})`);
        if (translateY) t.push(`translateY(${translateY})`);
        
        if (t.length > 0) {
            setVar(`--b-${prefix}transform`, t.join(' '));
        }

        let f = [];
        const grayscale = baseProps[`${propPrefix}filter-grayscale`] ?? baseProps[`${propPrefix}filterGrayscale`];
        const blur = baseProps[`${propPrefix}filter-blur`] ?? baseProps[`${propPrefix}filterBlur`];
        
        if (grayscale) f.push(`grayscale(${grayscale})`);
        if (blur) f.push(`blur(${blur})`);
        
        if (f.length > 0) {
            setVar(`--b-${prefix}filter`, f.join(' '));
        }
    });

    // Map tablet properties
    setVar('--b-tablet-color', tProps['color']);
    setVar('--b-tablet-font-size', tProps['font-size']);
    setVar('--b-tablet-font-weight', tProps['font-weight']);
    setVar('--b-tablet-font-style', tProps['font-style']);
    setVar('--b-tablet-text-decoration', tProps['text-decoration']);
    setVar('--b-tablet-text-transform', tProps['text-transform']);
    setVar('--b-tablet-line-height', tProps['line-height']);
    setVar('--b-tablet-letter-spacing', tProps['letter-spacing']);
    setVar('--b-tablet-padding', tProps['padding']);
    setVar('--b-tablet-margin', tProps['margin']);
    setVar('--b-tablet-width', tProps['width']);
    setVar('--b-tablet-height', tProps['height']);
    setVar('--b-tablet-min-width', tProps['min-width']);
    setVar('--b-tablet-max-width', tProps['max-width']);
    setVar('--b-tablet-min-height', tProps['min-height']);
    setVar('--b-tablet-max-height', tProps['max-height']);
    setVar('--b-tablet-gap', tProps['gap']);
    setVar('--b-tablet-border-width', tProps['border-width']);
    setVar('--b-tablet-border-color', tProps['border-color']);
    setVar('--b-tablet-border-radius', tProps['border-radius']);
    setVar('--b-tablet-box-shadow', tProps['box-shadow']);
    setVar('--b-tablet-transition', tProps['transition']);
    setVar('--b-tablet-opacity', tProps['opacity']);
    setVar('--b-tablet-bg-color', tProps['bg-color']);
    setVar('--b-tablet-justify-content', tProps['justify-content']);
    setVar('--b-tablet-flex-direction', tProps['flex-direction']);
    setVar('--b-tablet-align-items', tProps['align-items']);
    setVar('--b-tablet-flex-wrap', tProps['flex-wrap']);
    setVar('--b-tablet-text-align', tProps['text-align']);
    setVar('--b-tablet-list-style-type', tProps['list-style-type']);

    // Map mobile properties
    setVar('--b-mobile-color', mProps['color']);
    setVar('--b-mobile-font-size', mProps['font-size']);
    setVar('--b-mobile-font-weight', mProps['font-weight']);
    setVar('--b-mobile-font-style', mProps['font-style']);
    setVar('--b-mobile-text-decoration', mProps['text-decoration']);
    setVar('--b-mobile-text-transform', mProps['text-transform']);
    setVar('--b-mobile-line-height', mProps['line-height']);
    setVar('--b-mobile-letter-spacing', mProps['letter-spacing']);
    setVar('--b-mobile-padding', mProps['padding']);
    setVar('--b-mobile-margin', mProps['margin']);
    setVar('--b-mobile-width', mProps['width']);
    setVar('--b-mobile-height', mProps['height']);
    setVar('--b-mobile-min-width', mProps['min-width']);
    setVar('--b-mobile-max-width', mProps['max-width']);
    setVar('--b-mobile-min-height', mProps['min-height']);
    setVar('--b-mobile-max-height', mProps['max-height']);
    setVar('--b-mobile-gap', mProps['gap']);
    setVar('--b-mobile-border-width', mProps['border-width']);
    setVar('--b-mobile-border-color', mProps['border-color']);
    setVar('--b-mobile-border-radius', mProps['border-radius']);
    setVar('--b-mobile-box-shadow', mProps['box-shadow']);
    setVar('--b-mobile-transition', mProps['transition']);
    setVar('--b-mobile-opacity', mProps['opacity']);
    setVar('--b-mobile-bg-color', mProps['bg-color']);
    setVar('--b-mobile-justify-content', mProps['justify-content']);
    setVar('--b-mobile-flex-direction', mProps['flex-direction']);
    setVar('--b-mobile-align-items', mProps['align-items']);
    setVar('--b-mobile-flex-wrap', mProps['flex-wrap']);
    setVar('--b-mobile-text-align', mProps['text-align']);
    setVar('--b-mobile-list-style-type', mProps['list-style-type']);

    // For cms-col elements, override flex:1 behavior so width works
    if (baseProps['width'] && baseProps.className && baseProps.className.includes('cms-col')) {
        bVars.flex = baseProps['flex'] || '0 0 auto';
    }

    const hoverClass = [
        baseProps['hover-bg-color'] ? 'has-hover-bg' : '',
        baseProps['hover-color'] ? 'has-hover-color' : '',
        baseProps['hover-border-color'] ? 'has-hover-border' : '',
        baseProps['active-bg-color'] ? 'has-active-bg' : ''
    ].filter(Boolean).join(' ');

    const visibilityClass = [
        baseProps.hideDesktop ? 'd-none-desktop' : '',
        baseProps.hideTablet ? 'd-none-tablet' : '',
        baseProps.hideMobile ? 'd-none-mobile' : ''
    ].filter(Boolean).join(' ');

    const truncateClass = [
        baseProps['text-truncate'] ? `truncate-${baseProps['text-truncate']}` : '',
        tProps['text-truncate'] ? `t-truncate-${tProps['text-truncate']}` : '',
        mProps['text-truncate'] ? `m-truncate-${mProps['text-truncate']}` : ''
    ].filter(Boolean).join(' ');

    const noLayoutTypes = ['form_input', 'form_textarea', 'form_select', 'form_checkbox'];
    const isNativeUI = (baseProps.className && baseProps.className.includes('pagelet')) || element.element_type === 'form_container' || noLayoutTypes.includes(element.element_type);
    const layoutClass = isNativeUI ? '' : (isLayout ? 'seczone-layout' : 'seczone-element');

    const getResponsiveClasses = (props, prefix) => {
        if (!props) return [];
        const classes = [];
        const supportedProps = [
            'color', 'font-size', 'font-weight', 'font-style', 'text-decoration', 'text-transform',
            'padding', 'margin', 'width', 'height', 'min-width', 'max-width', 'min-height', 'max-height', 'gap', 'border-width',
            'border-color', 'border-radius', 'box-shadow', 'transition',
            'bg-color', 'justify-content', 'flex-direction', 'align-items',
            'flex-wrap', 'line-height', 'text-align', 'list-style-type'
        ];
        supportedProps.forEach(prop => {
            if (props[prop] !== undefined && props[prop] !== '') {
                classes.push(`resp-${prefix}-${prop}`);
            }
        });
        if (props['width'] && baseProps.className && baseProps.className.includes('cms-col')) {
            classes.push(`resp-${prefix}-col-width`);
        }
        return classes;
    };

    const responsiveClasses = [
        ...getResponsiveClasses(tProps, 't'),
        ...getResponsiveClasses(mProps, 'm')
    ].join(' ');

    const elementClass = `${layoutClass} ${hoverClass} ${visibilityClass} ${truncateClass} ${responsiveClasses} ${baseProps.className || ''}`.trim();

    let Tag = element.tag || 'div';
    let displayContent = dynamicData ? replacePlaceholders(element.content) : element.content;

    // Smart Tag Detection for legacy/content
    if (!element.tag && (element.element_type === 'text' || element.element_type === 'richtext') && element.content) {
        const match = element.content.trim().match(/^<([a-z1-6]+|a)(?:\s+[^>]*)?>(.*)<\/\1>$/is);
        if (match) {
            Tag = match[1].toLowerCase();
            displayContent = dynamicData ? replacePlaceholders(match[2]) : match[2];
        }
    }

    switch (element.element_type) {
        case 'text':
        case 'richtext':
            return (
                <Tag
                    ref={elementRef}
                    className={elementClass}
                    style={bVars}
                    href={Tag === 'a' ? (baseProps.href || '#') : undefined}
                    target={Tag === 'a' ? baseProps.target : undefined}
                    rel={Tag === 'a' && baseProps.target === '_blank' ? 'noopener noreferrer' : undefined}
                    title={baseProps.title}
                    aria-label={baseProps['aria-label']}
                    type={Tag === 'ol' ? baseProps.type : undefined}
                    dangerouslySetInnerHTML={{ __html: displayContent }}
                />
            );
        case 'button':
            const btnAlign = (bVars['--b-text-align'] || '') === 'left' ? 'align-left' : (bVars['--b-text-align'] || '') === 'right' ? 'align-right' : 'align-center';
            const buttonStyleVal = baseProps['button-style'] || '';
            const buttonVariantClass = [
                baseProps['button-variant'] || '',
                tProps['button-variant'] ? `tablet-${tProps['button-variant']}` : '',
                mProps['button-variant'] ? `mobile-${mProps['button-variant']}` : ''
            ].filter(Boolean).join(' ');

            const buttonSizeClass = [
                baseProps['button-size'] || '',
                tProps['button-size'] ? `tablet-${tProps['button-size']}` : '',
                mProps['button-size'] ? `mobile-${mProps['button-size']}` : ''
            ].filter(Boolean).join(' ');

            const { iconLeft, iconRight, iconGap = '0px' } = baseProps;

            const btnDynamicStyle = { ...bVars };

            // Map explicit override colors to CSS variables for the cascade
            if (baseProps['bg-color']) btnDynamicStyle['--btn-bg'] = baseProps['bg-color'];
            if (baseProps['color']) btnDynamicStyle['--btn-color'] = baseProps['color'];
            if (baseProps['border-radius']) btnDynamicStyle['--btn-border-radius'] = baseProps['border-radius'];

            // Map hover/active colors to CSS variables for the cascade
            if (baseProps['hover-bg-color']) {
                btnDynamicStyle['--btn-hover-bg'] = baseProps['hover-bg-color'];
            }
            if (baseProps['hover-color']) {
                btnDynamicStyle['--btn-hover-color'] = baseProps['hover-color'];
            }
            if (baseProps['active-bg-color']) {
                btnDynamicStyle['--btn-active-bg'] = baseProps['active-bg-color'];
            }

            // Also output responsive button variables for live CSS cascade
            if (tProps['hover-bg-color']) btnDynamicStyle['--btn-hover-bg'] = tProps['hover-bg-color'];
            if (tProps['hover-color']) btnDynamicStyle['--btn-hover-color'] = tProps['hover-color'];
            if (tProps['active-bg-color']) btnDynamicStyle['--btn-active-bg'] = tProps['active-bg-color'];
            if (tProps['border-radius']) btnDynamicStyle['--btn-tablet-border-radius'] = tProps['border-radius'];

            if (mProps['hover-bg-color']) btnDynamicStyle['--btn-hover-bg'] = mProps['hover-bg-color'];
            if (mProps['hover-color']) btnDynamicStyle['--btn-hover-color'] = mProps['hover-color'];
            if (mProps['active-bg-color']) btnDynamicStyle['--btn-active-bg'] = mProps['active-bg-color'];
            if (mProps['border-radius']) btnDynamicStyle['--btn-mobile-border-radius'] = mProps['border-radius'];

            const buttonStyleClass = buttonStyleVal;

            const btnClass = `button ${elementClass} ${btnAlign} ${buttonStyleClass} ${buttonVariantClass} ${buttonSizeClass}`.trim();

            const isPopupMenu = baseProps.isPopupMenu === 'true' || baseProps.isPopupMenu === true;
            const ButtonTag = (baseProps.href && !isPopupMenu) ? 'a' : 'button';
            const buttonProps = (baseProps.href && !isPopupMenu) ? {
                href: baseProps.href,
                target: baseProps.target,
                rel: baseProps.target === '_blank' ? 'noopener noreferrer' : undefined,
                title: baseProps.title,
                'aria-label': baseProps['aria-label']
            } : {
                type: baseProps.type || 'button',
                title: baseProps.title,
                'aria-label': baseProps['aria-label'],
                onClick: (e) => {
                    if (isPopupMenu) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const menuItems = baseProps.popupHeaderActions || [];
                        
                        const parsedItems = menuItems.map(a => ({
                            ...a,
                            onClick: () => { if (a.action) { try { eval(a.action) } catch(err){} } }
                        }));

                        openPopupMenu({
                            referenceElement: e.currentTarget,
                            content: <PopupDropdownMenu 
                                menuItems={parsedItems}
                            />,
                            placement: baseProps.popupPlacement || 'bottom-start'
                        });
                    }
                }
            };

            return (
                <ButtonTag ref={elementRef} className={btnClass} style={btnDynamicStyle} {...buttonProps}>
                    {iconLeft && <Icon icon={icons[iconLeft]} style={{ marginRight: iconGap }} />}
                    {element.content && <span>{dynamicData ? replacePlaceholders(element.content) : element.content}</span>}
                    {iconRight && <Icon icon={icons[iconRight]} style={{ marginLeft: iconGap }} />}
                </ButtonTag>
            );
        case 'image':
            const PLACEHOLDER_IMG = '/defaults/no-image.webp';
            const imgClass = `${elementClass} seczone-image`.trim();
            let imgSrc = (baseProps.src && !baseProps.src.startsWith('data:'))
                ? (baseProps.src === '/defaults/no-image.svg' ? '/defaults/no-image.webp' : baseProps.src)
                : (baseProps['bg-image'] || PLACEHOLDER_IMG);
            
            // If the src still contains unreplaced {{tags}} or slug patterns, show placeholder
            if (imgSrc && (imgSrc.includes('{{') || imgSrc.includes('}}'))) {
                imgSrc = PLACEHOLDER_IMG;
            }

            return (
                <div ref={elementRef} className={imgClass} style={bVars}>
                    <img
                        src={imgSrc}
                        alt={baseProps.alt || ''}
                        style={{ objectFit: bVars['--b-object-fit'] || 'cover' }}
                        onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                    />
                </div>
            );
        case 'video':
            const videoClass = `${elementClass} seczone-video`.trim();
            const videoKey = `vid-${baseProps.src || ''}-${baseProps.poster || ''}-${baseProps.controls !== false}-${!!baseProps.autoplay}-${!!baseProps.loop}-${!!baseProps.muted}`;
            return (
                <div ref={elementRef} className={videoClass} style={bVars}>
                    <video
                        key={videoKey}
                        ref={(el) => {
                            if (el) el.muted = !!baseProps.autoplay || !!baseProps.muted;
                        }}
                        src={baseProps.src || ''}
                        controls={baseProps.controls !== false}
                        autoPlay={!!baseProps.autoplay}
                        loop={!!baseProps.loop}
                        muted={!!baseProps.autoplay || !!baseProps.muted}
                        playsInline={true}
                        poster={baseProps.poster || undefined}
                        preload={(!baseProps.autoplay && baseProps.poster) ? "none" : "metadata"}
                        style={{ objectFit: bVars['--b-object-fit'] || baseProps['object-fit'] || 'contain' }}
                    >
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        case 'audio':
            const audioClass = `${elementClass} seczone-audio`.trim();
            const audioKey = `aud-${baseProps.src || ''}-${baseProps.controls !== false}-${!!baseProps.autoplay}-${!!baseProps.loop}-${!!baseProps.muted}`;
            return (
                <div ref={elementRef} className={audioClass} style={bVars}>
                    <audio
                        key={audioKey}
                        ref={(el) => {
                            if (el) el.muted = !!baseProps.autoplay || !!baseProps.muted;
                        }}
                        src={baseProps.src || ''}
                        controls={baseProps.controls !== false}
                        autoPlay={!!baseProps.autoplay}
                        loop={!!baseProps.loop}
                        muted={!!baseProps.autoplay || !!baseProps.muted}
                        preload={!baseProps.autoplay ? "none" : "auto"}
                        style={{ width: '100%' }}
                    >
                        Your browser does not support the audio element.
                    </audio>
                </div>
            );
        case 'container':
        case 'dynamic_data_grid': {
            let renderElements = elements;
            if (element.element_type === 'dynamic_data_grid' && dynamicData && dynamicData.id) {
                const findRecordId = (blk) => {
                    if (!blk) return null;
                    if (blk.properties?.href) {
                        const m = String(blk.properties.href).match(/\/(\d+)(?:[/?#]|$)/);
                        if (m) return parseInt(m[1], 10);
                    }
                    if (blk.properties?.src) {
                        const m = String(blk.properties.src).match(/\/(\d+)\/photo(?:[/?#]|$)/);
                        if (m) return parseInt(m[1], 10);
                    }
                    if (blk.elements && Array.isArray(blk.elements)) {
                        for (let child of blk.elements) {
                            const id = findRecordId(child);
                            if (id !== null) return id;
                        }
                    }
                    return null;
                };

                renderElements = elements?.filter(subElement => {
                    const recordId = findRecordId(subElement);
                    if (recordId !== null && String(recordId) === String(dynamicData.id)) {
                        return false;
                    }
                    return true;
                });
            }
            return (
                <div ref={elementRef} className={elementClass} style={bVars}>
                    {renderElements?.map((subElement, idx) => (
                        <CmsElementRenderer
                            key={`${parentPath}-${idx}`}
                            element={subElement}
                            sectionId={sectionId}
                            parentPath={`${parentPath}-${idx}`}
                            dynamicData={dynamicData}
                        />
                    ))}
                </div>
            );
        }
        case 'icon':
            const IconTag = baseProps.href ? 'a' : 'div';
            const iconWrapClass = `${elementClass} seczone-icon ${baseProps['hover-scale'] === 'zoom' ? 'hover-zoom' : ''} ${baseProps['hover-rotate'] === 'rotate' ? 'hover-rotate' : ''} ${baseProps['hover-grayscale'] === 'grayscale' ? 'hover-grayscale' : ''} ${baseProps['hover-blur'] === 'blur' ? 'hover-blur' : ''} ${baseProps['hover-elevate'] === 'elevate' ? 'hover-elevate' : ''} ${baseProps['hover-bg-color'] ? 'has-hover-bg' : ''} ${baseProps['hover-color'] ? 'has-hover-color' : ''} ${baseProps['active-bg-color'] ? 'has-active-bg' : ''}`.trim();
            const iconDynamicStyle = { ...bVars };
            if (baseProps.color) iconDynamicStyle['--b-color'] = baseProps.color;

            const iconLinkProps = baseProps.href ? {
                href: baseProps.href,
                target: baseProps.target,
                rel: baseProps.target === '_blank' ? 'noopener noreferrer' : undefined,
                title: baseProps.title,
                'aria-label': baseProps['aria-label']
            } : {
                title: baseProps.title,
                'aria-label': baseProps['aria-label']
            };

            return (
                <IconTag ref={elementRef} className={iconWrapClass} style={iconDynamicStyle} {...iconLinkProps}>
                    <Icon
                        icon={icons[baseProps.icon || 'info']}
                        size={baseProps.size || 24}
                    />
                </IconTag>
            );
        case 'form_container': {
            const fId = `form-cont-${sectionId}-${parentPath ? parentPath.replace(/[^a-zA-Z0-9]/g, '-') : 'root'}`;
            const userClasses = baseProps.innerClass !== undefined ? baseProps.innerClass : 'pagelet';
            const formLayoutClass = baseProps.layout === 'grid' ? 'form-grid-layout' : 'form-stack-layout';
            const containerClassName = `${elementClass} ${formLayoutClass} ${userClasses}`.trim();
            
            if (baseProps.layout === 'grid' && baseProps.gridColumns && baseProps.gridColumns !== 'auto') {
                bVars['--form-grid-cols'] = `repeat(${baseProps.gridColumns}, 1fr)`;
            }
            if (baseProps.globalInputGap) {
                bVars['--form-gap'] = baseProps.globalInputGap;
            }

            const handleFormSubmit = async (e) => {
                e.preventDefault();
                setFormState({ loading: true, success: false, error: null });
                
                if (baseProps.systemValidation !== false) {
                    const form = e.target;
                    let isValid = true;
                    
                    const elements = form.querySelectorAll('input, select, textarea');
                    elements.forEach(el => {
                        const caseEl = el.closest('.input-case');
                        if (!caseEl) return;
                        
                        caseEl.classList.remove('invalid');
                        const existingErr = caseEl.querySelector('em.error-msg');
                        if (existingErr) existingErr.remove();
                        
                        if (!el.checkValidity()) {
                            isValid = false;
                            caseEl.classList.add('invalid');
                            
                            const err = document.createElement('em');
                            err.className = 'error-msg';
                            err.textContent = el.validationMessage || 'This field is required';
                            caseEl.appendChild(err);
                        }
                    });

                    if (!isValid) {
                        setFormState({ loading: false, success: false, error: 'Please fill in all required fields correctly.' });
                        return;
                    }
                }

                if (!baseProps.tableName) {
                    setFormState({ loading: false, success: false, error: 'Form configuration error: No Target Table selected.' });
                    return;
                }
                
                const formData = new FormData(e.target);
                const data = Object.fromEntries(formData.entries());
                
                try {
                    const submitUrl = `/api/cms/dynamic-forms/submit/${baseProps.tableName}`;
                        
                    const response = await fetch(submitUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(data),
                    });
                    
                    if (response.ok) {
                        const onSuccessAction = baseProps.onSuccess || 'none';
                        if (onSuccessAction === 'redirect' && baseProps.redirectUrl) {
                            if (baseProps.redirectNewTab) {
                                window.open(baseProps.redirectUrl, '_blank');
                                setFormState({ loading: false, success: false, error: null });
                            } else {
                                window.location.href = baseProps.redirectUrl;
                            }
                            e.target.reset();
                        } else if (onSuccessAction === 'popup') {
                            showAlert({
                                title: 'Success',
                                content: baseProps.successMessage || 'Thank you! Your submission has been received.',
                                style: 'success'
                            });
                            setFormState({ loading: false, success: false, error: null });
                            e.target.reset();
                        } else {
                            setFormState({ loading: false, success: true, error: null });
                            e.target.reset();
                        }
                    } else {
                        const resData = await response.json();
                        throw new Error(resData.message || 'Failed to submit form');
                    }
                } catch (err) {
                    console.error(err);
                    setFormState({ loading: false, success: false, error: err.message || 'Failed to submit form. Please try again.' });
                }
            };
            
            const handleFormInput = (e) => {
                if (baseProps.systemValidation !== false) {
                    const el = e.target;
                    const caseEl = el.closest('.input-case');
                    if (caseEl && caseEl.classList.contains('invalid')) {
                        if (el.checkValidity()) {
                            caseEl.classList.remove('invalid');
                            const existingErr = caseEl.querySelector('em.error-msg');
                            if (existingErr) existingErr.remove();
                        }
                    }
                }
            };
            
            return (
                <FormContext.Provider value={baseProps}>
                    <form id={fId} ref={elementRef} className={containerClassName} style={bVars} onSubmit={handleFormSubmit} onInput={handleFormInput} noValidate={baseProps.systemValidation !== false}>
                        {formState.success ? (
                            <div className="form-success-message" style={{ padding: '20px', textAlign: 'center', background: '#e6ffe6', color: '#006600', borderRadius: '8px' }}>
                                {baseProps.successMessage || 'Thank you! Your submission has been received.'}
                            </div>
                        ) : (
                            <>
                                {formState.error && (
                                    <div className="form-error-message" style={{ padding: '10px', marginBottom: '15px', background: '#ffe6e6', color: '#cc0000', borderRadius: '4px' }}>
                                        {formState.error}
                                    </div>
                                )}
                                {elements?.map((subElement, idx) => (
                                    <CmsElementRenderer
                                        key={`${parentPath}-${idx}`}
                                        element={subElement}
                                        sectionId={sectionId}
                                        parentPath={`${parentPath}-${idx}`}
                                    />
                                ))}
                                {formState.loading && (
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                                        Loading...
                                    </div>
                                )}
                            </>
                        )}
                    </form>
                </FormContext.Provider>
            );
        }
        case 'form_row':
            return (
                <div ref={elementRef} className={`form-row ${elementClass}`} style={bVars}>
                    {elements?.map((subElement, idx) => (
                        <CmsElementRenderer
                            key={`${parentPath}-${idx}`}
                            element={subElement}
                            sectionId={sectionId}
                            parentPath={`${parentPath}-${idx}`}
                        />
                    ))}
                </div>
            );
        case 'form_input': {
            const fId = `fld-${sectionId}-${parentPath ? parentPath.replace(/[^a-zA-Z0-9]/g, '-') : 'root'}`;
            const rightClass = baseProps.iconRight ? 'right' : '';
            const finalLabelColor = baseProps.labelColor || formParentProps.globalLabelColor;
            const finalLabelFontSize = baseProps.labelFontSize || formParentProps.globalLabelFontSize;
            const finalLabelFontWeight = baseProps.labelFontWeight || formParentProps.globalLabelFontWeight;

            const finalInputPadding = baseProps.inputPadding || formParentProps.globalInputPadding;
            const finalInputHeight = baseProps.inputHeight || formParentProps.globalInputHeight;
            const finalInputBg = baseProps.inputBg || formParentProps.globalInputBg;
            const finalInputBorder = baseProps.inputBorder || formParentProps.globalInputBorder;
            const finalInputBorderRadius = baseProps.inputBorderRadius || formParentProps.globalInputBorderRadius;
            
            return (
                <div ref={elementRef} id={`${fId}-case`} className={`input-case ${baseProps.widthClass || ''} ${elementClass}`} style={bVars}>
                    <style>{`
                        #${fId} {
                            ${finalInputPadding ? `padding: ${finalInputPadding} !important;` : ''}
                            ${finalInputHeight ? `height: ${finalInputHeight} !important;` : ''}
                            ${finalInputBorder ? `border: ${finalInputBorder} !important;` : ''}
                            ${finalInputBorderRadius ? `border-radius: ${finalInputBorderRadius} !important;` : ''}
                            ${finalInputBg ? `background-color: ${finalInputBg} !important;` : ''}
                        }
                        #${fId}-case > label.label {
                            ${finalLabelColor ? `color: ${finalLabelColor} !important;` : ''}
                            ${finalLabelFontSize ? `font-size: ${finalLabelFontSize} !important;` : ''}
                            ${finalLabelFontWeight ? `font-weight: ${finalLabelFontWeight} !important;` : ''}
                        }
                        @media (max-width: 900px) { #${fId} { ${tProps?.inputHeight ? `height: ${tProps.inputHeight};` : ''} } }
                        @media (max-width: 600px) { #${fId} { ${mProps?.inputHeight ? `height: ${mProps.inputHeight};` : ''} } }
                    `}</style>
                    {baseProps.label && <label className="label">{baseProps.label} {baseProps.required ? <span className="required">*</span> : null}</label>}
                    <div className={`input-group ${baseProps.iconLeft ? 'left' : ''} ${rightClass}`}>
                        <input 
                            id={fId}
                            type={baseProps.type || 'text'} 
                            name={baseProps.columnName || baseProps.name} 
                            placeholder={baseProps.placeholder} 
                            required={baseProps.required} 
                            autoComplete={baseProps.autoCompleteEnabled ? (baseProps.autoComplete || 'on') : 'off'}
                            className={`${baseProps.iconLeft ? 'left' : ''} ${rightClass}`}
                        />
                        {baseProps.iconLeft && <Icon icon={icons[baseProps.iconLeft] || baseProps.iconLeft} className="input-icon" />}
                        {baseProps.iconRight && <Icon icon={icons[baseProps.iconRight] || baseProps.iconRight} className="input-icon right" />}
                    </div>
                </div>
            );
        }
        case 'form_textarea': {
            const fId = `fld-${sectionId}-${parentPath ? parentPath.replace(/[^a-zA-Z0-9]/g, '-') : 'root'}`;
            const finalLabelColor = baseProps.labelColor || formParentProps.globalLabelColor;
            const finalLabelFontSize = baseProps.labelFontSize || formParentProps.globalLabelFontSize;
            const finalLabelFontWeight = baseProps.labelFontWeight || formParentProps.globalLabelFontWeight;

            const finalInputPadding = baseProps.inputPadding || formParentProps.globalInputPadding;
            const finalInputHeight = baseProps.inputHeight || formParentProps.globalInputHeight;
            const finalInputBg = baseProps.inputBg || formParentProps.globalInputBg;
            const finalInputBorder = baseProps.inputBorder || formParentProps.globalInputBorder;
            const finalInputBorderRadius = baseProps.inputBorderRadius || formParentProps.globalInputBorderRadius;

            return (
                <div ref={elementRef} id={`${fId}-case`} className={`input-case ${baseProps.widthClass || ''} ${elementClass}`} style={bVars}>
                    <style>{`
                        #${fId} {
                            ${finalInputPadding ? `padding: ${finalInputPadding} !important;` : ''}
                            ${finalInputHeight ? `height: ${finalInputHeight} !important;` : ''}
                            ${finalInputBorder ? `border: ${finalInputBorder} !important;` : ''}
                            ${finalInputBorderRadius ? `border-radius: ${finalInputBorderRadius} !important;` : ''}
                            ${finalInputBg ? `background-color: ${finalInputBg} !important;` : ''}
                        }
                        #${fId}-case > label.label {
                            ${finalLabelColor ? `color: ${finalLabelColor} !important;` : ''}
                            ${finalLabelFontSize ? `font-size: ${finalLabelFontSize} !important;` : ''}
                            ${finalLabelFontWeight ? `font-weight: ${finalLabelFontWeight} !important;` : ''}
                        }
                        @media (max-width: 900px) { #${fId} { ${tProps?.inputHeight ? `height: ${tProps.inputHeight};` : ''} } }
                        @media (max-width: 600px) { #${fId} { ${mProps?.inputHeight ? `height: ${mProps.inputHeight};` : ''} } }
                    `}</style>
                    {baseProps.label && <label className="label">{baseProps.label} {baseProps.required ? <span className="required">*</span> : null}</label>}
                    <div className="input-group">
                        <textarea 
                            id={fId}
                            name={baseProps.columnName || baseProps.name} 
                            placeholder={baseProps.placeholder} 
                            required={baseProps.required} 
                            rows={baseProps.rows || 4} 
                            style={{ resize: 'vertical' }} 
                        />
                    </div>
                </div>
            );
        }
        case 'form_select': {
            const formOptions = (baseProps.options || 'Option 1').split(',');
            const fId = `fld-${sectionId}-${parentPath ? parentPath.replace(/[^a-zA-Z0-9]/g, '-') : 'root'}`;
            const finalLabelColor = baseProps.labelColor || formParentProps.globalLabelColor;
            const finalLabelFontSize = baseProps.labelFontSize || formParentProps.globalLabelFontSize;
            const finalLabelFontWeight = baseProps.labelFontWeight || formParentProps.globalLabelFontWeight;

            const finalInputPadding = baseProps.inputPadding || formParentProps.globalInputPadding;
            const finalInputHeight = baseProps.inputHeight || formParentProps.globalInputHeight;
            const finalInputBg = baseProps.inputBg || formParentProps.globalInputBg;
            const finalInputBorder = baseProps.inputBorder || formParentProps.globalInputBorder;
            const finalInputBorderRadius = baseProps.inputBorderRadius || formParentProps.globalInputBorderRadius;

            return (
                <div ref={elementRef} id={`${fId}-case`} className={`input-case ${baseProps.widthClass || ''} ${elementClass}`} style={bVars}>
                    <style>{`
                        #${fId} {
                            ${finalInputPadding ? `padding: ${finalInputPadding} !important;` : ''}
                            ${finalInputHeight ? `height: ${finalInputHeight} !important;` : ''}
                            ${finalInputBorder ? `border: ${finalInputBorder} !important;` : ''}
                            ${finalInputBorderRadius ? `border-radius: ${finalInputBorderRadius} !important;` : ''}
                            ${finalInputBg ? `background-color: ${finalInputBg} !important;` : ''}
                        }
                        #${fId}-case > label.label {
                            ${finalLabelColor ? `color: ${finalLabelColor} !important;` : ''}
                            ${finalLabelFontSize ? `font-size: ${finalLabelFontSize} !important;` : ''}
                            ${finalLabelFontWeight ? `font-weight: ${finalLabelFontWeight} !important;` : ''}
                        }
                        @media (max-width: 900px) { #${fId} { ${tProps?.inputHeight ? `height: ${tProps.inputHeight};` : ''} } }
                        @media (max-width: 600px) { #${fId} { ${mProps?.inputHeight ? `height: ${mProps.inputHeight};` : ''} } }
                    `}</style>
                    {baseProps.label && <label className="label">{baseProps.label} {baseProps.required ? <span className="required">*</span> : null}</label>}
                    <div className="input-group">
                        <select 
                            id={fId}
                            name={baseProps.columnName || baseProps.name} 
                            required={baseProps.required} 
                        >
                            {formOptions.map((opt, i) => <option key={i} value={opt.trim()}>{opt.trim()}</option>)}
                        </select>
                    </div>
                </div>
            );
        }
        case 'form_checkbox':
            return (
                <div ref={elementRef} className={`input-case ${baseProps.widthClass || ''} ${elementClass}`} style={bVars}>
                    <ul className="radio-case" style={{ margin: 0, padding: 0 }}>
                        <li>
                            <label>
                                <input type="checkbox" name={baseProps.columnName || baseProps.name} required={baseProps.required} />
                                {baseProps.label} {baseProps.required ? <span className="required">*</span> : null}
                            </label>
                        </li>
                    </ul>
                </div>
            );
        case 'form_switch':
            return (
                <div ref={elementRef} className={`input-case ${baseProps.widthClass || ''} ${elementClass}`} style={bVars}>
                    {baseProps.label && <p style={{ marginBottom: '5px' }}>{baseProps.label} {baseProps.required ? <span className="required">*</span> : null}</p>}
                    <div className="toggle-switch">
                        <label style={{ margin: 0 }}>
                            <input type="checkbox" name={baseProps.columnName || baseProps.name} required={baseProps.required} />
                            <span></span>
                        </label>
                    </div>
                </div>
            );
        case 'form_radio': {
            const formOptions = (baseProps.options || 'Option 1,Option 2').split(',');
            return (
                <div ref={elementRef} className={`input-case ${baseProps.widthClass || ''} ${elementClass}`} style={bVars}>
                    {baseProps.label && <p style={{ marginBottom: '5px' }}>{baseProps.label} {baseProps.required ? <span className="required">*</span> : null}</p>}
                    <ul className="radio-case" style={{ margin: 0, padding: 0, display: 'flex', flexDirection: baseProps.layout === 'horizontal' ? 'row' : 'column', gap: baseProps.layout === 'horizontal' ? '15px' : '0px' }}>
                        {formOptions.map((opt, i) => (
                            <li key={i}>
                                <label style={{ margin: 0 }}>
                                    <input type="radio" name={baseProps.columnName || baseProps.name} value={opt.trim()} required={baseProps.required && i === 0} />
                                    {opt.trim()}
                                </label>
                            </li>
                        ))}
                    </ul>
                </div>
            );
        }
        default:
            return (
                <div
                    ref={elementRef}
                    className={elementClass}
                    style={bVars}
                    dangerouslySetInnerHTML={{ __html: element.content }}
                />
            );
    }
};

export default CmsElementRenderer;
